import { useState, useEffect } from 'react';
import { Hotel } from '@types';
import { hotelService } from '../services';

/**
 * Hook for fetching a single hotel by ID
 */
export const useHotel = (hotelId: string | undefined) => {
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchHotelData = async (bustCache: boolean = false): Promise<Hotel | null> => {
    if (!hotelId) {
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      const data = await hotelService.getHotelById(hotelId, bustCache);
      setHotel(data);
      setNotFound(false);
      setError(null);

      if (bustCache) {
        console.log('✅ Hotel data refreshed with cache-busting:', {
          hotelId,
          estimatedValue: data.estimated_value,
          lastValuationDate: data.last_valuation_date,
          fetchTimestamp: new Date().toISOString()
        });
      }

      return data;
    } catch (error: any) {
      console.error('Error fetching hotel:', error);
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        setError(error.response?.data?.error || 'Failed to load hotel details');
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotelData();
  }, [hotelId]);

  return {
    hotel,
    loading,
    error,
    notFound,
    fetchHotelData,
    setHotel,
    setError
  };
};